import { type MutableRefObject, useCallback, useMemo, useRef } from 'react';
import DocumentTitle from 'react-document-title';
import { useParams } from 'react-router-dom';

import { type AppHeaderIndexProps } from '@hivebrite/design-system/AppHeader';
import { useBreakpoint } from '@hivebrite/design-system/Theme';
import { useScrollTop } from '@hivebrite/react-hooks';

import MediaCenterBreadcrumb from 'hb-react/frontoffice/apps/MediaCenter/components/Breadcrumb';
import FilesRenderer from 'hb-react/frontoffice/apps/MediaCenter/components/FilesRenderer';
import MediaCenterFolderPageHeader from 'hb-react/frontoffice/apps/MediaCenter/components/FolderPageHeader';
import FoldersRenderer from 'hb-react/frontoffice/apps/MediaCenter/components/FoldersRenderer';
import {
  FilesTitle,
  FoldersTitle,
} from 'hb-react/frontoffice/apps/MediaCenter/components/ItemsTitles';
import { EmptyFolderRenderer } from 'hb-react/frontoffice/apps/MediaCenter/container/EmptyFolderRenderer';
import { useFilesAndFoldersId } from 'hb-react/frontoffice/apps/MediaCenter/container/hooks/useFilesAndFoldersId';
import {
  EMPTY_CONTENT_ID,
  FILTERS_ID,
  ITEMS_BY_ROW,
  SORT_BY,
  TAGS_LIMIT,
} from 'hb-react/frontoffice/apps/MediaCenter/container/utils/rendererConstants';
import {
  getDataFiltersForFiles,
  getDataFiltersForFolders,
  getMediaCenterHeaderProps,
  makeFetchFiles,
  makeFetchFolders,
  makeFilters,
  scrollToFilesBlock,
} from 'hb-react/frontoffice/apps/MediaCenter/container/utils/rendererUtils';
import MediaCenterPreviewModal from 'hb-react/frontoffice/apps/MediaCenter/MediaCenterPreviewModal';
import {
  type MediaCenterCategory,
  type MediaCenterFolder,
} from 'hb-react/frontoffice/apps/MediaCenter/types';
import {
  fetchFilesUrl,
  fetchFoldersUrl,
} from 'hb-react/frontoffice/apps/MediaCenter/utils/mediaCenterUrls';
import BlocksRenderer from 'hb-react/frontoffice/components/BlocksRenderer';
import { BLOCKS } from 'hb-react/frontoffice/components/BlocksRenderer/shared';
import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { type Topic } from 'hb-react/shared/apps/Topics/containers/TopicShowContainer/types';
import ThumbnailLoader from 'hb-react/shared/components/Thumbnails/ThumbnailLoader';
import { PAGES, usePageMenuName } from 'hb-react/shared/hooks/usePageMenuName';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';
import { getPageTitleFromNetwork } from 'hb-react/shared/utils/getPageTitleFromNetwork';
import { type AppPageConfig } from 'hb-react/types';

interface BlockFiltersRef {
  clearFilters: () => void;
}

interface BlockItemsRef {
  checkHasTags: () => boolean;
  fetchNextPage: () => void;
  resetTags: () => void;
}

interface MediaCenterBlocksRendererProps {
  configPage: AppPageConfig;
  currentFolder?: MediaCenterFolder;
  isFolderEmpty: boolean;
  isLoadingCurrentFolder: boolean;
  tags: Array<MediaCenterCategory> | null;
  topic?: Topic;
}

interface MediaCenterBlocksRendererContentProps {
  appHeaderProps: AppHeaderIndexProps;
  currentFolder?: MediaCenterFolder;
  isLoadingCurrentFolder: boolean;
  tags: Array<MediaCenterCategory> | null;
  topic?: Topic;
}

const MediaCenterBlocksRenderer = ({
  configPage,
  currentFolder,
  topic,
  isFolderEmpty,
  isLoadingCurrentFolder,
  tags,
}: MediaCenterBlocksRendererProps) => {
  const appHeaderProps = getMediaCenterHeaderProps({
    appPageConfig: configPage,
  });

  if (isFolderEmpty) {
    return (
      <EmptyFolderRenderer topic={topic} appHeaderProps={appHeaderProps} />
    );
  }

  return (
    <MediaCenterBlocksRendererContent
      appHeaderProps={appHeaderProps}
      currentFolder={currentFolder}
      topic={topic}
      isLoadingCurrentFolder={isLoadingCurrentFolder}
      tags={tags}
    />
  );
};

const MediaCenterBlocksRendererContent = ({
  appHeaderProps,
  currentFolder,
  topic,
  isLoadingCurrentFolder,
  tags,
}: MediaCenterBlocksRendererContentProps) => {
  const { currentNetwork } = useDefaultAppContexts();
  const {
    headerHeight = 0,
    headerHeightMobile = 0,
    topicMenuHeight = 0,
  } = useUIContext();

  const mediaCenterConfig = currentNetwork.network_configuration.media_center;

  const initialFolderSort = SORT_BY.find(
    (element) => element.value === mediaCenterConfig?.default_folder_sort,
  );

  const initialFileSort = SORT_BY.find(
    (element) => element.value === mediaCenterConfig?.default_file_sort,
  );

  const { fileUuid, folderUuid } = useParams<{
    fileUuid?: string;
    folderUuid?: string;
  }>();
  const breakpoint = useBreakpoint();

  const filesRef = useRef<BlockItemsRef | null>(null);
  const filterRef = useRef<BlockFiltersRef | null>(null);
  const folderUuidRef: MutableRefObject<string | undefined> =
    useRef(folderUuid); // folderUuid is kept as a ref to not be lost on opening a file preview

  const { filesId, foldersId } = useFilesAndFoldersId(folderUuidRef);

  useScrollTop([folderUuidRef.current]);

  const scrollToFilesBlockOnClick = useCallback(() => {
    const height = breakpoint === 's' ? headerHeightMobile : headerHeight;

    scrollToFilesBlock(folderUuidRef.current, height + topicMenuHeight);
  }, [breakpoint, headerHeight, headerHeightMobile, topicMenuHeight]);

  const filters = useMemo(
    () => makeFilters(scrollToFilesBlockOnClick),
    [scrollToFilesBlockOnClick],
  );

  const fetchFiles = makeFetchFiles(
    fetchFilesUrl({ folderUuid: folderUuidRef.current, currentNetwork, topic }),
  );

  const fetchFolders = makeFetchFolders(
    fetchFoldersUrl({
      folderUuid: folderUuidRef.current,
      currentNetwork,
      breakpoint,
      topic,
    }),
  );

  const topicTabName = usePageMenuName(PAGES.MEDIA_CENTER, __('Media Center'));

  return (
    <>
      <DocumentTitle
        title={generatePageTitle({
          title: currentFolder?.name || topicTabName,
          networkName: getPageTitleFromNetwork(currentNetwork),
          topicName: topic?.name,
        })}
      />
      <BlocksRenderer
        templateProps={{
          topicId: topic?.id,
          hasFilters: filters?.length > 0,
        }}
        blocks={[
          {
            ...BLOCKS.appHeader,
            attributes: {
              ...appHeaderProps,
            },
          },
          {
            id: FILTERS_ID,
            ref: filterRef,
            component: BLOCKS.filters.component,
            attributes: {
              filters,
            },
          },
          () => (
            <MediaCenterBreadcrumb
              currentFolder={currentFolder}
              isLoading={isLoadingCurrentFolder}
            />
          ),
          () => (
            <MediaCenterFolderPageHeader
              currentFolder={currentFolder}
              isLoading={isLoadingCurrentFolder}
            />
          ),
          {
            id: foldersId,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems: fetchFolders,
              getDataFilters: getDataFiltersForFolders,
              itemLoader: ThumbnailLoader,
              itemRenderer: FoldersRenderer,
              itemsByRow: ITEMS_BY_ROW[breakpoint],
              loaderLines: 1,
              shouldHideIfEmpty: true,
              infiniteLoad: false,
              title: FoldersTitle,
              sortBy: SORT_BY,
              initialSort: initialFolderSort,
              resultsFoundMessage: (itemsSize) =>
                n__(
                  '%{itemsSize} folder found',
                  '%{itemsSize} folders found',
                  itemsSize,
                ).i__({
                  itemsSize: `${itemsSize}`,
                }),
              noResultsMessage: __('No folders found'),
              columnsInMobile: true,
            },
          },
          {
            id: filesId,
            ref: filesRef,
            component: BLOCKS.items.component,
            attributes: {
              fetchItems: fetchFiles,
              getDataFilters: getDataFiltersForFiles,
              itemLoader: ThumbnailLoader,
              itemRenderer: FilesRenderer,
              itemsByRow: ITEMS_BY_ROW[breakpoint],
              loaderLines: 1,
              shouldHideIfEmpty: true,
              infiniteLoad: true,
              tags: {
                tags: tags || [],
                position: 'after-title',
                multi: true,
                limit: TAGS_LIMIT[breakpoint],
              },
              title: FilesTitle,
              sortBy: SORT_BY,
              initialSort: initialFileSort,
              resultsFoundMessage: (itemsSize) =>
                n__(
                  '%{itemsSize} file found',
                  '%{itemsSize} files found',
                  itemsSize,
                ).i__({
                  itemsSize: `${itemsSize}`,
                }),
              noResultsMessage: __('No files found'),
              columnsInMobile: true,
            },
          },
          {
            id: EMPTY_CONTENT_ID,
            component: BLOCKS.emptyContent.component,
            attributes: {
              shouldDisplay: ({ data }) =>
                data?.[filesId]?.length === 0 &&
                data?.[foldersId]?.length === 0 &&
                !filesRef?.current?.checkHasTags() &&
                !data?.[FILTERS_ID],
              title: s__('MediaCenter|No content has been posted yet.'),
            },
          },
        ]}
      />
      {fileUuid && (
        <MediaCenterPreviewModal
          fetchNextPage={filesRef?.current?.fetchNextPage}
          folderUuid={folderUuidRef.current}
          topicName={topic?.name}
        />
      )}
    </>
  );
};

export default MediaCenterBlocksRenderer;
